import { useCallback, useRef, type RefObject } from 'react'
import { toast } from 'sonner'

import { useMountEffect } from '@/hooks/useMountEffect'
import {
  EMPTY_QUEUE,
  useMessageQueue,
  type QueuedMessage,
} from '@/stores/messageQueue'

/**
 * Loads a queued item into the composer while the store marks it as being
 * edited. The user starts and ends an edit through the handlers here; the
 * store is watched only for the item leaving the queue underneath an edit
 * (drain, promote, or its Remove button).
 */
export function useQueuedMessageEdit({
  conversationId,
  input,
  setInput,
  textareaRef,
}: {
  conversationId: string | undefined
  input: string
  setInput: (text: string) => void
  textareaRef: RefObject<HTMLTextAreaElement | null>
}) {
  // Live mirrors, so the handlers stay stable and the store listener, which
  // outlives any one render, reads the current values.
  const inputRef = useRef(input)
  inputRef.current = input
  const conversationIdRef = useRef(conversationId)
  conversationIdRef.current = conversationId
  // The composer text from before an edit began, restored when it ends.
  const draftRef = useRef('')

  const beginEdit = useCallback(
    (item: QueuedMessage) => {
      if (!conversationId) return
      draftRef.current = inputRef.current
      setInput(item.text)
      useMessageQueue.getState().setEditing(conversationId, item.id)
      textareaRef.current?.focus()
    },
    [conversationId, setInput, textareaRef]
  )

  const cancelEdit = useCallback(() => {
    if (!conversationId) return
    useMessageQueue.getState().setEditing(conversationId, null)
    setInput(draftRef.current)
  }, [conversationId, setInput])

  const commitEdit = useCallback(
    (id: string, text: string) => {
      if (!conversationId) return
      useMessageQueue.getState().edit(conversationId, id, text)
      setInput(draftRef.current)
    },
    [conversationId, setInput]
  )

  useMountEffect(() => {
    const unsubscribe = useMessageQueue.subscribe((state, prev) => {
      const cid = conversationIdRef.current
      if (!cid) return
      const before = prev.queues[cid] ?? EMPTY_QUEUE
      if (!before.editingId) return
      const next = state.queues[cid] ?? EMPTY_QUEUE
      if (next.items.some((i) => i.id === before.editingId)) return
      const original = before.items.find((i) => i.id === before.editingId)
      if (inputRef.current === original?.text) {
        setInput(draftRef.current)
        return
      }
      toast('Queued message is no longer queued', {
        description: 'Your unsaved edit was kept in the composer.',
      })
    })
    return () => {
      unsubscribe()
      // The composer unmounts on a send or a conversation switch; the edit
      // text goes with it, so the item must not stay marked as being edited.
      const cid = conversationIdRef.current
      if (cid) useMessageQueue.getState().setEditing(cid, null)
    }
  })

  return { beginEdit, cancelEdit, commitEdit }
}
